import React, { useState } from 'react';

import { Pill } from '../../../../components/pill/Pill';
import classNames from '../../../../utils/classnames';
import { formatNumber } from '../../../../utils/number';
import { BAR_FILL_CLASS, getWeekTargetLevel } from './weekTarget';

interface WeekProgressProps {
  km: number;
  targetKm: number | null;
  /** When set, the target is editable inline. */
  onTargetChange?: (targetKm: number) => Promise<void>;
}

/** Week column cell: planned km against the week target, with a coloured progress bar. */
export const WeekProgress: React.FC<WeekProgressProps> = ({ km, targetKm, onTargetChange }) => {
  const percentage = targetKm ? Math.round((km / targetKm) * 100) : null;
  const level = percentage == null ? null : getWeekTargetLevel(percentage);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-1">
          <span className="text-sm font-semibold text-fg">{formatNumber(km, 0)}</span>
          {onTargetChange ? (
            <TargetInput targetKm={targetKm} onSubmit={onTargetChange} />
          ) : (
            <span className="text-2xs text-fg-muted">/ {targetKm == null ? '–' : formatNumber(targetKm, 0)} km</span>
          )}
        </div>
        {level && <Pill color={level}>{percentage}%</Pill>}
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-hover">
        {level && (
          <div
            className={classNames('h-full rounded-full', BAR_FILL_CLASS[level])}
            style={{ width: `${Math.min(percentage ?? 0, 100)}%` }}
          />
        )}
      </div>
    </div>
  );
};

interface TargetInputProps {
  targetKm: number | null;
  onSubmit: (targetKm: number) => Promise<void>;
}

/** `/ 2.400 km` that turns into a number field on click. */
const TargetInput: React.FC<TargetInputProps> = ({ targetKm, onSubmit }) => {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = async () => {
    if (draft == null) return;
    const value = draft.trim() === '' ? null : Number(draft);
    setDraft(null);
    if (value == null || !Number.isInteger(value) || value < 0 || value === targetKm) return;
    await onSubmit(value);
  };

  if (draft != null) {
    return (
      <span className="flex items-baseline gap-1 text-2xs text-fg-muted">
        /
        <input
          autoFocus
          inputMode="numeric"
          aria-label="Weektarget in km"
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setDraft(null);
          }}
          className="w-14 rounded-sm border border-brand bg-surface px-1 text-2xs text-fg outline-hidden"
        />
        km
      </span>
    );
  }

  return (
    <button
      type="button"
      title="Weektarget aanpassen"
      onClick={() => setDraft(targetKm == null ? '' : String(targetKm))}
      className="truncate rounded-sm text-2xs text-fg-muted underline-offset-2 hover:text-fg-secondary hover:underline"
    >
      {targetKm == null ? 'Target instellen' : `/ ${formatNumber(targetKm, 0)} km`}
    </button>
  );
};
